import { ExclamationCircleOutlined } from '@ant-design/icons'
import { Select, Tooltip } from 'antd'
import { useTranslation } from 'contexts/Localization'
import moment from 'moment-timezone'
import Link from 'next/link'
import { useTheme } from 'next-themes'
import { Dispatch, SetStateAction } from 'react'
import styled from 'styled-components'
import { addCommasToNumber } from 'utils'
import { ArrowBackIcon, Box } from 'widgets'
import { IWalletSummary } from 'config/types/Wallet'

const Wrapper = styled.div`
  width: 100%;

  .ant-select-selector {
    padding: 0 !important;
    border: none !important;
    box-shadow: none !important;
  }

  .ant-select-selection-item {
    font-size: 14px;
  }
`

const TIME_FORMAT = 'YYYY-MM-DD'

const timeOptions = (t) => [
  {
    label: t('7 days'),
    value: moment().subtract(7, 'days').format(TIME_FORMAT),
  },
  {
    label: t('30 days'),
    value: moment().subtract(30, 'days').format(TIME_FORMAT),
  },
  {
    label: t('90 days'),
    value: moment().subtract(90, 'days').format(TIME_FORMAT),
  },
]

type StakingPropType = {
  // balances: any
  // usdtPrice: number
  walletSummary: IWalletSummary
  rate: {
    rateTotalBalance?: number
    rateComAvailable?: number
    rateComTotal?: number
    rateComWithdraw?: number
    rateComLimit?: number
    rateStakingEarn: number
    rateStakingLock: number
  }
  setParamStakingEarn: Dispatch<
    SetStateAction<{
      range: number
      type: string
    }>
  >
  setParamStakingLock: Dispatch<
    SetStateAction<{
      range: number
      type: string
    }>
  >
}

const Staking = ({ walletSummary, rate, setParamStakingEarn, setParamStakingLock }: StakingPropType) => {
  const { t } = useTranslation()
  const { theme } = useTheme()

  const isDark = theme === 'dark'

  const stakingData = [
    {
      id: 1,
      label: 'Total earned',
      tooltip: 'Total staked rewards available for withdrawal',
      balance: walletSummary?.totalStakingEarn,
      percent: rate?.rateStakingEarn,
    },
    {
      id: 2,
      label: 'Total Locked',
      tooltip: 'Total staked rewards earned',
      balance: walletSummary?.totalStakingLock,
      percent: rate?.rateStakingLock,
    },
  ]

  const handleFilterRate = (type: string, val: number) => {
    if (type === 'Total earned') {
      setParamStakingEarn((prev) => ({ ...prev, range: val }))
      return
    }

    setParamStakingLock((prev) => ({ ...prev, range: val }))
  }

  return (
    <Wrapper className="box-primary">
      <div className="flex md:flex-row flex-col md:items-center md:justify-between md:gap-0 gap-4 xl:px-6 py-6 px-4 border-b-2 border-solid stroke-brand-alpha-1">
        <p className="text-[18px] leading-6 font-bold text-primary">{t('Staking wallet')}</p>

        <Link href="/staking" passHref>
          <Box className="flex items-center gap-2 cursor-pointer">
            <p className="text-[14px] text-secondary leading-6">{t('View more')}</p>
            <ArrowBackIcon fill="#989996" className="rotate-[180deg]" width={16} />
          </Box>
        </Link>
      </div>

      <div className="grid md:grid-cols-2 divide-y-2 md:divide-y-0 md:divide-x-2 divide-solid divide-brand-alpha-10-light dark:divide-brand-alpha-10-dark">
        {stakingData?.map((item) => (
          <div className="flex flex-col gap-1 xl:px-6 py-6 px-4" key={item?.id}>
            <div className="flex items-center justify-between gap-1">
              <div className="flex items-center gap-1">
                <p className="text-[14px] text-secondary leading-6">{t(item?.label)}</p>
                <Tooltip title={t(item?.tooltip)}>
                  <ExclamationCircleOutlined className="text-secondary w-[16px] h-[16px]" rev={undefined} />
                </Tooltip>
              </div>

              <Select
                className="w-[100px]"
                options={[
                  {
                    label: t('in 7 days'),
                    value: 7,
                  },
                  {
                    label: t('in 30 days'),
                    value: 30,
                  },
                  {
                    label: t('in 90 days'),
                    value: 90,
                  },
                ]}
                defaultValue={7}
                onChange={(val) => handleFilterRate(item?.label, val)}
              />
            </div>

            <div className="flex items-center justify-between gap-1">
              <div className="flex md:flex-row flex-col gap-1 items-center">
                <p className="text-[14px] font-bold leading-6 text-primary">{addCommasToNumber(item?.balance)}</p>
                <p className="text-[14px] text-secondary leading-6">≈${addCommasToNumber(item?.balance * 1)}</p>
              </div>

              <div className="flex items-center space-x-1">
                <ArrowBackIcon className="rotate-[90deg] w-4" fill="#3AB500" />
                <p className="text-text-success text-[14px] leading-6">{item?.percent}%</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Wrapper>
  )
}

export default Staking
